import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { apiClient, formatSAR } from "../lib/api";
import VehicleCard from "../components/VehicleCard";
import Seo from "../components/Seo";
import Wordmark from "../components/Wordmark";
import AdSlot from "../components/AdSlot";
import EscrowTrustBanner from "../components/EscrowTrustBanner";
import OwnerJourney from "../components/OwnerJourney";
import LocationPicker from "../components/LocationPicker";
import ResumeBrowsingBanner from "../components/ResumeBrowsingBanner";
import LaunchCampaignBanners from "../components/LaunchCampaignBanners";
import Vision2030Badge from "../components/Vision2030Badge";
import LiveAuctionsStrip from "../components/LiveAuctionsStrip";
import LiveAuctionAlertBadge from "../components/LiveAuctionAlertBadge";
import { FEATURES } from "../lib/features";
import { GOV_LINKS } from "../lib/govLinks";
import {
  ArrowRight, ArrowUpRight, Search, Sparkles, ShieldCheck, Clock, CircleDollarSign,
  Loader2, Plus, MapPin, Receipt,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=2000&q=85&auto=format&fit=crop";

const MODULES_ALL = [
  { to: "/marketplace?type=new", titleEn: "New Vehicles", titleAr: "السيارات الجديدة", n: "01" },
  { to: "/marketplace?type=used", titleEn: "Used Vehicles", titleAr: "المستعملة", n: "02" },
  { to: "/rentals", titleEn: "Rentals", titleAr: "التأجير", n: "03" },
  { to: "/auctions", titleEn: "Auctions", titleAr: "المزادات", n: "04", feature: "AUCTIONS_ENABLED" },
  { to: "/financing", titleEn: "Financing", titleAr: "التمويل", n: "05" },
  { to: "/insurance", titleEn: "Insurance", titleAr: "التأمين", n: "06" },
  { to: "/inspection", titleEn: "Inspection", titleAr: "الفحص", n: "07" },
  { to: "/services", titleEn: "Maintenance", titleAr: "الصيانة", n: "08" },
  { to: "/parts", titleEn: "Spare Parts", titleAr: "قطع الغيار", n: "09" },
  { to: "/delivery", titleEn: "Delivery", titleAr: "التوصيل", n: "10" },
  { to: "/salvage", titleEn: "Salvage", titleAr: "السكراب", n: "11" },
  { to: "/export", titleEn: "Export", titleAr: "التصدير", n: "12" },
  { to: "/heavy", titleEn: "Heavy Equipment", titleAr: "المعدات الثقيلة", n: "13" },
  { to: "/motorcycles", titleEn: "Motorcycles", titleAr: "الدراجات", n: "14" },
  { to: "/marine", titleEn: "Yachts & Boats", titleAr: "يخوت وزوارق", n: "15" },
  { to: "/fleet", titleEn: "Corporate Fleet", titleAr: "أساطيل الشركات", n: "16" },
];

const MODULES = MODULES_ALL
  .filter((m) => !m.feature || FEATURES[m.feature])
  .map((m, i) => ({ ...m, n: String(i + 1).padStart(2, "0") }));

function Hero() {
  const { t, lang } = useI18n();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const smartSearch = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post("/ai/smart-search", { text: q });
      const params = new URLSearchParams();
      Object.entries(data.filters || {}).forEach(([k, v]) => {
        if (v) params.set(k, String(v));
      });
      nav(`/marketplace?${params.toString()}`);
    } catch {
      nav(`/marketplace?q=${encodeURIComponent(q)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#e8e3d6] via-[#ece7da] to-[#faf8f1]">
      <ResumeBrowsingBanner />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 pt-14 pb-16 lg:pt-20 lg:pb-20">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-14 items-center">
          <div>
            {/* Flashing red live-auctions alert — only renders when at least one auction is active */}
            {FEATURES.AUCTIONS_ENABLED && <LiveAuctionAlertBadge />}
            <div className="overline overline-gold mb-5 inline-flex items-center gap-2">
              <Sparkles className="w-3 h-3" />
              {lang === "ar" ? "السوبر-آب الأول للسيارات في السعودية" : "Saudi Arabia's #1 automotive super-app"}
            </div>
            <h1 className="font-display text-[40px] sm:text-6xl lg:text-7xl tracking-[-0.03em] leading-[0.98] font-semibold text-[#0a0a0a]">
              {lang === "ar" ? (
                <>قُد أكثر.<br /><span className="text-[#8a6f2e]">عِش أفضل.</span></>
              ) : (
                <>Drive more.<br /><span className="text-[#8a6f2e]">Live better.</span></>
              )}
            </h1>

            {/* Brand statement / slogan */}
            <div className="mt-5 inline-flex flex-col gap-1.5">
              <p className="font-display text-lg sm:text-xl text-[#0a0a0a] font-bold leading-snug">
                {lang === "ar"
                  ? "أي سيارة. أي خدمة. تحت سقف واحد."
                  : "Every car. Every service. Under one roof."}
              </p>
              <p className="text-sm sm:text-base text-[#2a2a2a] leading-snug max-w-lg font-medium">
                {lang === "ar"
                  ? "أكبر منصة لبيع وشراء وتأجير وتمويل السيارات في الشرق الأوسط."
                  : "The Middle East's largest platform to buy, sell, rent & finance vehicles."}
              </p>
            </div>

            <p className="text-sm sm:text-base text-[#3a3a3a] mt-5 max-w-xl leading-relaxed font-medium">
              {lang === "ar"
                ? "اشترِ، بِع، استأجر، موّل، أمّن، افحص، انقل، وزايد على المركبات — في منصة واحدة ذكية."
                : "Buy, sell, rent, finance, insure, inspect, transport and bid on vehicles — all in one smart platform."}
            </p>

            {/* AI Smart Search */}
            <form onSubmit={smartSearch} className="mt-7 group" data-testid="ai-search-form">
              <div className="flex items-stretch gap-2 bg-white border border-[#0a0a0a]/15 rounded-full p-1.5 shadow-sm focus-within:border-[#c9a55c] focus-within:shadow-[0_0_0_4px_rgba(201,165,92,0.2)] transition-all">
                <div className="relative flex-1 flex items-center">
                  <Sparkles className="absolute start-3 w-4 h-4 text-[#8a6f2e]" />
                  <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={lang === "ar"
                      ? "جرّب البحث الذكي: «جيب عائلي تحت 200 ألف في الرياض»"
                      : "Try smart search: \"family SUV under 200k in Riyadh\""}
                    className="w-full bg-transparent border-0 outline-none ps-10 py-2.5 text-[#0a0a0a] placeholder:text-[#8a8a8a] text-sm"
                    data-testid="hero-search-input"
                  />
                </div>
                <button type="submit" className="btn-primary px-5" disabled={busy} data-testid="hero-search-button">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  {busy ? (lang === "ar" ? "بحث…" : "Searching…") : (lang === "ar" ? "ابحث بالذكاء" : "Smart search")}
                </button>
              </div>
              <p className="text-[11px] text-[#6b6b6b] mt-2 ms-4">
                {lang === "ar"
                  ? "مدعوم بـClaude AI — يفهم الجمل العربية والإنجليزية"
                  : "Powered by Claude AI — understands Arabic and English"}
              </p>
            </form>

            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-sm text-[#3a3a3a]" data-testid="hero-categories">
              {[
                { to: "/marketplace?type=used", labelEn: "Used", labelAr: "مستعمل" },
                { to: "/marketplace?type=new", labelEn: "New", labelAr: "جديد" },
                ...(FEATURES.AUCTIONS_ENABLED ? [{ to: "/auctions", labelEn: "Auctions", labelAr: "المزادات" }] : []),
                { to: "/rentals", labelEn: "Rentals", labelAr: "تأجير" },
                { to: "/salvage", labelEn: "Salvage", labelAr: "سكراب" },
                { to: "/export", labelEn: "Export", labelAr: "تصدير" },
                { to: "/heavy", labelEn: "Heavy", labelAr: "ثقيلة" },
                { to: "/motorcycles", labelEn: "Motorcycles", labelAr: "دراجات" },
                { to: "/marine", labelEn: "Yachts & Boats", labelAr: "يخوت وزوارق" },
              ].map((c) => (
                <Link key={c.to} to={c.to} className="hover:text-[#0a0a0a] border-b border-transparent hover:border-[#c9a55c] pb-0.5 transition-colors" data-testid={`hero-cat-${c.to}`}>
                  {lang === "ar" ? c.labelAr : c.labelEn}
                </Link>
              ))}
            </div>
          </div>

          {/* Hero visual: full official logo on the brand-correct dark background */}
          <div className="relative flex justify-center items-center">
            <div className="absolute -inset-6 bg-gradient-to-br from-[#c9a55c]/30 via-transparent to-transparent rounded-full blur-3xl pointer-events-none" />
            <div
              className="relative rounded-3xl w-full max-w-[460px] aspect-square overflow-hidden flex items-center justify-center luxury-frame"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_carvo-market/artifacts/c5durw9k_B010DD54-BD83-49AB-A5A3-946EA832B9AF.png"
                alt="CARTAL — Drive more. Live better."
                className="w-[88%] h-[88%] object-contain"
                data-testid="hero-logo"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  const { lang } = useI18n();
  const items = [
    FEATURES.ESCROW_ENABLED && { icon: ShieldCheck, t: lang === "ar" ? "ضمان CARTAL" : "CARTAL Escrow", d: lang === "ar" ? "أموالك آمنة حتى الاستلام" : "Funds held until delivery" },
    { icon: Sparkles, t: lang === "ar" ? "تقييم ذكي AI" : "AI valuation", d: lang === "ar" ? "اعرف القيمة العادلة فوراً" : "Know fair value instantly" },
    { icon: Clock, t: lang === "ar" ? "فحص في 60 دقيقة" : "60-min inspection", d: lang === "ar" ? "تقرير شامل قبل الشراء" : "Full report before you buy" },
    { icon: CircleDollarSign, t: lang === "ar" ? "تمويل من 3.49٪" : "Financing from 3.49%", d: lang === "ar" ? "أكبر 7 بنوك ومموّلين" : "7 top banks & lenders" },
    // 4th slot (replaces the hidden CARTAL Escrow tile) — opens the official
    // Absher "Pay traffic violations" service in a new tab. URL sourced from
    // `lib/govLinks.js` so ops can rotate it without a code change.
    { icon: Receipt, t: lang === "ar" ? "تسديد المخالفات المرورية" : "Pay traffic violations", d: lang === "ar" ? "عبر منصة أبشر الرسمية" : "Via official Absher portal", href: GOV_LINKS.trafficFines },
  ].filter(Boolean);
  return (
    <section className="border-y border-[#0a0a0a]/[0.06] bg-white">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map((it, i) => {
          const Icon = it.icon;
          const inner = (
            <>
              <div className="w-10 h-10 rounded-xl bg-[#c9a55c]/10 border border-[#c9a55c]/30 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-[#a8893e]" />
              </div>
              <div>
                <div className="text-sm font-semibold text-[#0a0a0a]">{it.t}</div>
                <div className="text-xs text-[#525252] mt-0.5">{it.d}</div>
              </div>
            </>
          );
          if (it.href) {
            return (
              <a
                key={it.t}
                href={it.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3"
                data-testid={`trust-${i}`}
              >
                {inner}
              </a>
            );
          }
          return (
            <div key={it.t} className="flex items-start gap-3" data-testid={`trust-${i}`}>
              {inner}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function HomeMarketBrowser() {
  const { lang } = useI18n();
  const [type, setType] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // iter279 — progressive loading state. `hasMore` is derived from the
  // last server response: if the API returned fewer rows than the
  // batch size we know we hit the tail. `loadingMore` gates repeat
  // clicks and drives the in-button spinner.
  const PAGE_SIZE = 12;
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const TABS = [
    { id: "", ar: "الكل", en: "All" },
    { id: "new", ar: "جديدة", en: "New" },
    // iter276 — home page discovery tab, independent of the "new" tab.
    { id: "new_customs_card", ar: "🆕 بطاقة جمركية", en: "🆕 Customs Card" },
    { id: "used", ar: "مستعملة", en: "Used" },
    { id: "rental", ar: "تأجير", en: "Rentals" },
  ];

  // Initial + type-switch load. Resets pagination cleanly whenever the
  // tab changes so we never mix rows from two different filters.
  useEffect(() => {
    setLoading(true);
    setItems([]);
    setHasMore(false);
    const q = { limit: PAGE_SIZE, skip: 0 };
    if (type) q.type = type;
    apiClient
      .get("/vehicles", { params: q })
      .then(({ data }) => {
        const rows = data.items || [];
        setItems(rows);
        setHasMore(rows.length === PAGE_SIZE);
      })
      .catch(() => { setItems([]); setHasMore(false); })
      .finally(() => setLoading(false));
  }, [type]);

  // "Load More" handler — appends the next batch beneath the existing
  // ones. Guards against concurrent clicks and never resets scroll.
  const loadMore = () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const q = { limit: PAGE_SIZE, skip: items.length };
    if (type) q.type = type;
    apiClient
      .get("/vehicles", { params: q })
      .then(({ data }) => {
        const next = data.items || [];
        // Belt-and-braces dedupe by id — server-side skip should
        // already prevent duplicates, but the id-set filter makes it
        // impossible for a same-second write to leak a duplicate row.
        setItems((cur) => {
          const seen = new Set(cur.map((it) => it.id));
          const uniqueNext = next.filter((it) => !seen.has(it.id));
          return cur.concat(uniqueNext);
        });
        setHasMore(next.length === PAGE_SIZE);
      })
      .catch(() => { /* keep existing rows; leave button re-enabled */ })
      .finally(() => setLoadingMore(false));
  };

  return (
    <section className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-20" data-testid="home-market-browser">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="overline overline-gold mb-3">{lang === "ar" ? "تصفَّح السوق" : "Browse the market"}</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] font-medium max-w-xl text-[#0a0a0a]">
            {lang === "ar" ? "اختر ما يناسبك من أحدث الإعلانات" : "Pick what suits you from the latest listings"}
          </h2>
        </div>
        <Link to="/marketplace" className="btn-ghost" data-testid="home-view-all">
          {lang === "ar" ? "عرض الكل" : "View all"} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="flex flex-wrap gap-2 mb-8" data-testid="home-type-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id || "all"}
            onClick={() => setType(tab.id)}
            className={`pill text-xs sm:text-sm ${type === tab.id ? "pill-cyan" : ""}`}
            data-testid={`home-tab-${tab.id || "all"}`}
          >
            {lang === "ar" ? tab.ar : tab.en}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#a8893e]" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-[#8a8a8a]" data-testid="home-empty">
          {lang === "ar" ? "لا توجد إعلانات مطابقة حالياً." : "No matching listings right now."}
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="home-listings-grid">
            {items.map((v) => <VehicleCard key={v.id} v={v} />)}
          </div>
          {/* iter279 — progressive "Load More" control. Sits below the
              grid so the user doesn't have to scroll back up to the
              "View all" link. Hidden entirely when tail is reached. */}
          <div className="flex justify-center mt-8">
            {hasMore ? (
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="btn-ghost text-sm px-5 py-2.5 disabled:opacity-60 disabled:cursor-wait"
                data-testid="home-load-more"
              >
                {loadingMore ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {lang === "ar" ? "جارٍ التحميل…" : "Loading…"}</>
                ) : (
                  <>{lang === "ar" ? "عرض المزيد من الإعلانات" : "Load more listings"}</>
                )}
              </button>
            ) : (
              <div className="text-xs text-[#8a8a8a]" data-testid="home-end-reached">
                {lang === "ar" ? "تم عرض جميع الإعلانات" : "You've reached the end"}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function ModulesList() {
  const { lang } = useI18n();
  return (
    <section className="bg-[#f5f4ef]">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-20">
        <div className="overline overline-gold mb-3">{lang === "ar" ? "منظومة كارتال" : "Cartal ecosystem"}</div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] font-medium max-w-3xl mb-12 text-[#0a0a0a]">
          {lang === "ar" ? "خمس عشرة وحدة. دورة حياة المركبة بالكامل." : "Fifteen modules. The entire vehicle lifecycle."}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#0a0a0a]/[0.08] border border-[#0a0a0a]/[0.08] rounded-2xl overflow-hidden">
          {MODULES.map((m) => (
            <Link
              key={m.to}
              to={m.to}
              className="group bg-white p-6 hover:bg-[#fafaf7] transition-colors flex flex-col justify-between min-h-[160px]"
              data-testid={`module-${m.n}`}
            >
              <span className="overline text-[#a5a5a5]">{m.n}</span>
              <div className="mt-10">
                <div className="font-display text-lg font-medium leading-tight text-[#0a0a0a] group-hover:text-[#a8893e]">
                  {lang === "ar" ? m.titleAr : m.titleEn}
                </div>
                <ArrowUpRight className="w-4 h-4 text-[#b3b3b3] group-hover:text-[#a8893e] mt-2 transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function AICallout() {
  const { lang } = useI18n();
  const [form, setForm] = useState({ make: "Toyota", model: "Camry", year: 2022, mileage_km: 35000, condition: "good" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setResult(null);
    try {
      const { data } = await apiClient.post("/ai/valuation", {
        ...form, year: Number(form.year), mileage_km: Number(form.mileage_km),
      });
      setResult(data);
    } finally { setLoading(false); }
  };

  return (
    <section className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-20 grid lg:grid-cols-2 gap-12 items-start">
      <div>
        <div className="overline overline-gold mb-3">CARTAL AI</div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] font-medium leading-tight text-[#0a0a0a]">
          {lang === "ar" ? "تقدير سعر فوري لمركبتك بالذكاء الاصطناعي." : "Instant AI-powered vehicle valuation."}
        </h2>
        <p className="text-[#525252] mt-4 leading-relaxed max-w-md">
          {lang === "ar"
            ? "مدعوم بـClaude Sonnet 4.5 ومُدرّب على سوق الخليج. اعرف القيمة العادلة قبل البيع أو الشراء — في 5 ثواني."
            : "Powered by Claude Sonnet 4.5, trained on GCC market data. Know the fair value before buying or selling — in 5 seconds."}
        </p>
      </div>

      <form onSubmit={submit} className="bg-white border border-[#0a0a0a]/[0.08] rounded-2xl p-7 space-y-3 shadow-sm" data-testid="ai-valuation-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label={lang === "ar" ? "الماركة" : "Make"} value={form.make} onChange={(v) => setForm({...form, make: v})} testid="ai-make" />
          <Field label={lang === "ar" ? "الموديل" : "Model"} value={form.model} onChange={(v) => setForm({...form, model: v})} testid="ai-model" />
          <Field label={lang === "ar" ? "السنة" : "Year"} value={form.year} onChange={(v) => setForm({...form, year: v})} type="number" testid="ai-year" />
          <Field label={lang === "ar" ? "العداد (كم)" : "Mileage (km)"} value={form.mileage_km} onChange={(v) => setForm({...form, mileage_km: v})} type="number" testid="ai-mileage" />
        </div>
        <button type="submit" className="btn-accent w-full justify-center" disabled={loading} data-testid="ai-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {loading ? (lang === "ar" ? "جاري التقييم…" : "Estimating…") : (lang === "ar" ? "قيّم الآن" : "Estimate value")}
        </button>
        {result && (
          <div className="pt-4 mt-2 border-t border-[#0a0a0a]/[0.08]" data-testid="ai-result">
            <div className="text-xs text-[#8a8a8a]">{lang === "ar" ? "القيمة العادلة" : "Fair value"}</div>
            <div className="font-display text-4xl mt-1 text-[#0a0a0a]">{formatSAR(result.fair_sar)} <span className="text-sm text-[#8a8a8a]">SAR</span></div>
            <div className="text-xs text-[#8a8a8a] mt-2">{lang === "ar" ? "النطاق:" : "Range:"} {formatSAR(result.low_sar)} – {formatSAR(result.high_sar)} SAR</div>
            <p className="text-sm text-[#525252] mt-3 leading-relaxed">{result.summary}</p>
          </div>
        )}
      </form>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-xs text-[#525252]">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="input-dark mt-1" data-testid={testid} />
    </label>
  );
}

function CitySection() {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [pickedCity, setPickedCity] = useState("");

  // Quick-jump chips — most popular cities in our coverage (Saudi + GCC).
  const QUICK = [
    { id: "Riyadh", ar: "الرياض", en: "Riyadh", flag: "🇸🇦" },
    { id: "Jeddah", ar: "جدة", en: "Jeddah", flag: "🇸🇦" },
    { id: "Dammam", ar: "الدمام", en: "Dammam", flag: "🇸🇦" },
    { id: "Makkah", ar: "مكة المكرمة", en: "Makkah", flag: "🇸🇦" },
    { id: "Madinah", ar: "المدينة المنورة", en: "Madinah", flag: "🇸🇦" },
    { id: "Khobar", ar: "الخبر", en: "Khobar", flag: "🇸🇦" },
    { id: "Dubai", ar: "دبي", en: "Dubai", flag: "🇦🇪" },
    { id: "Abu-Dhabi", ar: "أبوظبي", en: "Abu Dhabi", flag: "🇦🇪" },
    { id: "Doha", ar: "الدوحة", en: "Doha", flag: "🇶🇦" },
    { id: "Kuwait-City", ar: "مدينة الكويت", en: "Kuwait City", flag: "🇰🇼" },
    { id: "Manama", ar: "المنامة", en: "Manama", flag: "🇧🇭" },
    { id: "Muscat", ar: "مسقط", en: "Muscat", flag: "🇴🇲" },
  ];

  const goToCity = (id) => navigate(`/marketplace?city=${encodeURIComponent(id)}`);

  return (
    <section className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-20" data-testid="city-section">
      <div className="overline overline-gold mb-3">{lang === "ar" ? "تسوّق حسب الموقع" : "Shop by location"}</div>
      <h2 className="font-display text-3xl sm:text-4xl tracking-[-0.02em] font-medium mb-3 text-[#0a0a0a]">
        {lang === "ar" ? "خيارات في كل المملكة، الخليج، والدول المجاورة" : "Inventory across KSA, the Gulf & neighbouring countries"}
      </h2>
      <p className="text-[#525252] max-w-2xl mb-7 leading-relaxed">
        {lang === "ar"
          ? "اختر دولتك ومنطقتك ومدينتك من قائمة ذكية واحدة، أو اضغط على إحدى المدن الشائعة بالأسفل للوصول الفوري لإعلاناتها."
          : "Pick your country, region, and city from one smart selector — or tap a popular city below to jump straight to its listings."}
      </p>

      {/* Smart picker — replaces the old city tiles with landmark images */}
      <div className="max-w-2xl mb-6" data-testid="home-location-section">
        <LocationPicker
          value={pickedCity}
          onChange={(id) => {
            setPickedCity(id || "");
            if (id) goToCity(id);
          }}
          testId="home-location"
        />
      </div>

      {/* Popular-city quick-jump chips — no images, just clean pills */}
      <div className="flex flex-wrap gap-2" data-testid="city-quick-chips">
        {QUICK.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => goToCity(c.id)}
            className="inline-flex items-center gap-1.5 rounded-full bg-white text-[#3a3a3a] border-2 border-[#0a0a0a]/10 hover:border-[#c9a55c] hover:bg-[#fdf9ef] px-4 py-2 text-sm font-semibold transition-colors"
            data-testid={`city-quick-${c.id}`}
          >
            <span className="text-base leading-none">{c.flag}</span>
            <span>{lang === "ar" ? c.ar : c.en}</span>
            <ArrowRight className="w-3.5 h-3.5 text-[#8a6f2e] rtl:rotate-180" />
          </button>
        ))}
      </div>
    </section>
  );
}

function Vision2030Section() {
  const { lang } = useI18n();
  const stats = [
    {
      value: "100%",
      labelAr: "شركة سعودية",
      labelEn: "Saudi-owned",
      hintAr: "مقرّها ينبع الصناعية",
      hintEn: "Headquartered in Yanbu Industrial",
    },
    {
      value: "+16",
      labelAr: "خدمة رقمية",
      labelEn: "Digital services",
      hintAr: "تحت سقف واحد",
      hintEn: "Under one roof",
    },
    {
      value: "+85%",
      labelAr: "محتوى محلي مستهدف",
      labelEn: "Local content target",
      hintAr: "موردون ومطورون سعوديون",
      hintEn: "Saudi suppliers & developers",
    },
    {
      value: "24/7",
      labelAr: "ضمان وثقة",
      labelEn: "Escrow & trust",
      hintAr: "أموالك آمنة دائمًا",
      hintEn: "Funds always protected",
    },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#faf8f1] to-white">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-20">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-14 items-center">
          <div>
            <div className="overline overline-gold mb-4 inline-flex items-center gap-2" style={{ color: "#006C35" }}>
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: "#006C35" }} />
              {lang === "ar" ? "بفخر · شريك في الرؤية" : "Proudly · Partner in the vision"}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] font-semibold text-[#0a0a0a] leading-[1.05]">
              {lang === "ar" ? (
                <>كارتال تواكب <span style={{ color: "#006C35" }}>رؤية المملكة 2030</span></>
              ) : (
                <>CARTAL walks alongside <span style={{ color: "#006C35" }}>Saudi Vision 2030</span></>
              )}
            </h2>
            <p className="text-sm sm:text-base text-[#3a3a3a] mt-5 max-w-xl leading-relaxed font-medium">
              {lang === "ar"
                ? "نُسخّر التقنية والذكاء الاصطناعي لخلق اقتصاد سيارات رقمي شفّاف، يرفع نسبة المحتوى المحلي ويُمكّن المستهلك السعودي بأدوات ثقة عالمية."
                : "We harness AI and technology to build a transparent digital automotive economy — raising local content and empowering Saudi consumers with world-class trust tools."}
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-[#2a2a2a]" data-testid="vision-2030-pillars">
              {[
                { ar: "اقتصاد رقمي مزدهر", en: "Thriving digital economy" },
                { ar: "تمكين المستهلك ورفع الثقة", en: "Consumer empowerment & trust" },
                { ar: "محتوى محلي وفرص للشباب السعودي", en: "Local content & opportunities for Saudi youth" },
              ].map((p) => (
                <li key={p.en} className="flex items-start gap-2.5">
                  <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "#c9a55c" }} />
                  <span className="font-medium">{lang === "ar" ? p.ar : p.en}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl opacity-30 blur-2xl" style={{ background: "radial-gradient(circle at 30% 30%, #006C35, transparent 60%)" }} />
            <Vision2030Badge variant="hero" />
            <p className="text-[11px] text-[#8a8a8a] mt-3 text-center" dir={lang === "ar" ? "rtl" : "ltr"}>
              {lang === "ar"
                ? "هذا الشعار رمز تصميمي يعبّر عن مواكبتنا لرؤية المملكة 2030 — وليس الشعار الرسمي للرؤية."
                : "This emblem is a design tribute expressing our alignment with Saudi Vision 2030 — it is not the official Vision logo."}
            </p>
          </div>
        </div>

        {/* ── KPI strip — our contribution to the vision ────────── */}
        <div className="mt-14">
          <div className="text-center mb-8">
            <div className="overline overline-gold inline-block mb-2" style={{ color: "#006C35" }}>
              {lang === "ar" ? "إنجازاتنا في الرؤية" : "Our contribution to the vision"}
            </div>
            <h3 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] font-semibold text-[#0a0a0a]">
              {lang === "ar" ? "أرقام تترجم التزامنا" : "Numbers that translate our commitment"}
            </h3>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="vision-2030-kpis">
            {stats.map((s, i) => (
              <div
                key={s.labelEn}
                className="relative rounded-2xl p-5 sm:p-6 overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-lg"
                style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #f7f3e6 100%)",
                  border: "1px solid rgba(0,108,53,0.18)",
                }}
                data-testid={`vision-kpi-${i}`}
              >
                <div className="absolute top-0 end-0 w-20 h-20 opacity-[0.06] pointer-events-none" style={{ background: "radial-gradient(circle, #006C35 0%, transparent 70%)" }} />
                <div className="absolute bottom-0 start-0 end-0 h-[2px]" style={{ background: "linear-gradient(90deg, #006C35, #c9a55c)" }} />
                <div className="font-display text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: "#006C35" }}>
                  {s.value}
                </div>
                <div className="text-sm font-bold text-[#0a0a0a] mt-2">
                  {lang === "ar" ? s.labelAr : s.labelEn}
                </div>
                <div className="text-[11px] text-[#525252] mt-1 leading-snug">
                  {lang === "ar" ? s.hintAr : s.hintEn}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  const { lang } = useI18n();
  return (
    <section className="bg-[#0a0a0a] text-white">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24 text-center">
        <Wordmark variant="full" size="xl" className="mx-auto" />
        <p className="text-[#f1ecdf] mt-6 text-lg sm:text-xl max-w-xl mx-auto leading-relaxed font-semibold">
          {lang === "ar"
            ? "انضم إلى المنصة الأذكى للسيارات في المملكة العربية السعودية."
            : "Join Saudi Arabia's smartest automotive platform."}
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#c9a55c] hover:bg-[#d4b56e] text-[#0a0a0a] text-sm font-bold transition-colors" data-testid="cta-register">
            {lang === "ar" ? "أنشئ حسابك" : "Create account"}
            <ArrowRight className="w-4 h-4 rtl:rotate-180" />
          </Link>
          <Link to="/sell" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-[#c9a55c] text-[#c9a55c] hover:bg-[#c9a55c] hover:text-[#0a0a0a] transition-colors text-sm font-bold" data-testid="cta-list-vehicle">
            {lang === "ar" ? "أضف مركبتك" : "List a vehicle"}
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const { lang } = useI18n();
  // iter332 — WebSite + SearchAction. Enables Google sitelinks search box
  // and declares the canonical site identity next to the existing
  // Organization schema in index.html (which we deliberately preserve).
  const websiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "url": "https://cartal.sa/",
    "name": "CARTAL",
    "alternateName": "كارتال",
    "inLanguage": lang === "ar" ? "ar-SA" : "en",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://cartal.sa/marketplace?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };
  return (
    <div data-testid="home-page">
      <Seo
        title={lang === "ar" ? "كارتال | أي سيارة. أي خدمة. تحت سقف واحد." : "CARTAL | Every Vehicle. Every Service. One Platform."}
        description={lang === "ar" ? "منصة كارتال — أكبر سوق متكامل لبيع وشراء السيارات والمعدات الثقيلة والدراجات والقوارب وكل الخدمات المرتبطة بها في السعودية والشرق الأوسط." : "CARTAL — the Middle East's integrated marketplace for cars, heavy equipment, motorcycles, boats and every related service across Saudi Arabia."}
        jsonLd={websiteJsonLd}
      />
      <Hero />
      {FEATURES.ESCROW_ENABLED && <EscrowTrustBanner />}
      <TrustStrip />
      {/* homepage_top — dynamic house/paid ad campaign (ad_campaigns), restored
          alongside the iter164 launch-campaign banners below it. */}
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
        <AdSlot placement="homepage_top" variant="hero" />
      </div>
      <LaunchCampaignBanners />
      <HomeMarketBrowser />
      <OwnerJourney />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
        <AdSlot placement="homepage_mid" variant="banner" limit={2} />
      </div>
      <CitySection />
      <ModulesList />
      <AICallout />
      {FEATURES.AUCTIONS_ENABLED && <LiveAuctionsStrip />}
      <Vision2030Section />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
        <AdSlot placement="homepage_bottom" variant="banner" />
      </div>
      <CTASection />
    </div>
  );
}
